import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle2, MailCheck, XCircle } from 'lucide-react';
import { authAPI, profileAPI } from '../../../api/api.js';
import { useAuth } from '../../../context/AuthContext.js';
import { useLanguage } from '../../../context/LanguageContext.js';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner.js';
import { getAuthToken } from "../../../Utils/authToken.js";
import AuthBrand from '../AuthBrand/AuthBrand.jsx';

function isAlreadyVerifiedResponse(res) {
    if (res?.data?.alreadyVerified === true) return true;
    const msg = String(res?.data?.message || '').toLowerCase();
    return msg.includes('already verified');
}

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { updateUser } = useAuth();
    const { t } = useLanguage();
    const token = searchParams.get('token')?.trim() ?? '';
    const id = searchParams.get('id')?.trim() ?? '';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    /** True when they opened an old link but the account was already verified */
    const [alreadyVerified, setAlreadyVerified] = useState(false);

    const refreshProfileIfLoggedIn = useCallback(async () => {
        try {
            const tok = getAuthToken();
            if (!tok) return;
            const res = await profileAPI.getProfile();
            updateUser(res.data.admin);
        } catch {
            /* session may be invalid; UI still shows verification success */
        }
    }, [updateUser]);

    useEffect(() => {
        if (!token || !id) {
            setLoading(false);
            setError(t('auth.verifyEmail.missingParams'));
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const res = await authAPI.verifyEmail(token, id);
                if (cancelled) return;
                setMessage(res.data?.message || t('auth.verifyEmail.successDefault'));
                setAlreadyVerified(isAlreadyVerifiedResponse(res));
                await refreshProfileIfLoggedIn();
            } catch (err) {
                if (cancelled) return;
                setError(err.response?.data?.message || t('auth.verifyEmail.failedDefault'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [token, id, refreshProfileIfLoggedIn, t]);

    const handleRetry = async () => {
        if (!token || !id) return;
        setLoading(true);
        setError('');
        setMessage('');
        setAlreadyVerified(false);
        try {
            const res = await authAPI.verifyEmail(token, id);
            setMessage(res.data?.message || t('auth.verifyEmail.successDefault'));
            setAlreadyVerified(isAlreadyVerifiedResponse(res));
            await refreshProfileIfLoggedIn();
        } catch (err) {
            setError(err.response?.data?.message || t('auth.verifyEmail.failedDefault'));
        } finally {
            setLoading(false);
        }
    };

    const pageTitle = loading
        ? t('auth.verifyEmail.titles.confirming')
        : error
            ? t('auth.verifyEmail.titles.failed')
            : message && alreadyVerified
                ? t('auth.verifyEmail.titles.already')
                : message
                    ? t('auth.verifyEmail.titles.verified')
                    : !token || !id
                        ? t('auth.verifyEmail.titles.invalid')
                        : t('auth.verifyEmail.titles.confirming');

    const pageLede = loading
        ? t('auth.verifyEmail.ledes.loading')
        : error
            ? t('auth.verifyEmail.ledes.error')
            : message && alreadyVerified
                ? t('auth.verifyEmail.ledes.already')
                : message
                    ? t('auth.verifyEmail.ledes.success')
                    : !token || !id
                        ? t('auth.verifyEmail.ledes.invalid')
                        : '';

    return (
        <main className="auth-page">
            <AuthBrand corner to="/login" />
            <section className="verify-email-section">
                <div className="auth-shell container">
                    <div className="auth-card">
                        <p className="auth-eyebrow">{t('auth.verifyEmail.eyebrow')}</p>
                        <h2 className="auth-title">{pageTitle}</h2>
                        <p className="auth-lede">{pageLede}</p>

                        <div className="auth-fields" style={{ marginTop: '1.25rem' }}>
                            {loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                                    <LoadingSpinner message={t('auth.verifyEmail.verifying')} />
                                </div>
                            ) : null}

                            {!loading && message && alreadyVerified ? (
                                <div
                                    className="auth-status auth-status-info"
                                    role="status"
                                    style={{
                                        display: 'flex',
                                        gap: '0.75rem',
                                        alignItems: 'flex-start',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        background: 'rgba(59, 130, 246, 0.14)',
                                        border: '1px solid rgba(59, 130, 246, 0.25)',
                                        color: 'inherit',
                                    }}
                                >
                                    <MailCheck size={22} strokeWidth={2} aria-hidden style={{ flexShrink: 0, color: 'var(--auth-info-icon, #2563eb)' }} />
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 600 }}>{message}</p>
                                        <p style={{ margin: '0.5rem 0 0', opacity: 0.88 }}>
                                            {t('auth.verifyEmail.alreadyExtra')}
                                        </p>
                                        <button
                                            type="button"
                                            className="submitBtn"
                                            style={{ marginTop: '0.75rem', marginInlineEnd: '0.5rem' }}
                                            onClick={() => navigate('/home')}
                                        >
                                            {t('auth.verifyEmail.continueDashboard')}
                                        </button>
                                        <button
                                            type="button"
                                            className="submitBtn"
                                            style={{ marginTop: '0.75rem' }}
                                            onClick={() => navigate('/login')}
                                        >
                                            {t('auth.verifyEmail.goToLogin')}
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {!loading && message && !alreadyVerified ? (
                                <div
                                    className="auth-status auth-status-success"
                                    role="status"
                                    style={{
                                        display: 'flex',
                                        gap: '0.75rem',
                                        alignItems: 'flex-start',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        background: 'rgba(34, 197, 94, 0.12)',
                                        color: 'inherit',
                                    }}
                                >
                                    <CheckCircle2 size={22} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 600 }}>{message}</p>
                                        <p style={{ margin: '0.5rem 0 0', opacity: 0.85 }}>
                                            {t('auth.verifyEmail.successExtra')}
                                        </p>
                                        <button
                                            type="button"
                                            className="submitBtn"
                                            style={{ marginTop: '0.75rem', marginInlineEnd: '0.5rem' }}
                                            onClick={() => navigate('/home')}
                                        >
                                            {t('auth.verifyEmail.continueDashboard')}
                                        </button>
                                        <button
                                            type="button"
                                            className="submitBtn"
                                            style={{ marginTop: '0.75rem' }}
                                            onClick={() => navigate('/login')}
                                        >
                                            {t('auth.verifyEmail.goToLogin')}
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {!loading && error ? (
                                <div
                                    className="auth-status auth-status-error"
                                    role="alert"
                                    style={{
                                        display: 'flex',
                                        gap: '0.75rem',
                                        alignItems: 'flex-start',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        color: 'inherit',
                                    }}
                                >
                                    <XCircle size={22} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 600 }}>{error}</p>
                                        <p style={{ margin: '0.5rem 0 0', opacity: 0.85 }}>
                                            {t('auth.verifyEmail.errorHint')}
                                        </p>
                                        {token && id ? (
                                            <button
                                                type="button"
                                                className="submitBtn"
                                                style={{ marginTop: '0.75rem' }}
                                                onClick={handleRetry}
                                            >
                                                {t('auth.verifyEmail.tryAgain')}
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.7 }}>
                            <Mail size={18} strokeWidth={2} aria-hidden />
                            <span style={{ fontSize: '0.875rem' }}>{t('auth.verifyEmail.footer')}</span>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
